import { canBuildMarqueeCard } from '#src/app/pages/homepage/shared/utils';

import {
	buildCloseToGoalCard,
	buildMilestoneCard,
	buildMomentumCard,
	buildPetitionCard,
	buildSimplePetitionCard,
	buildSupporterVoiceCard,
	buildUpdateQuoteCard,
} from './builders';
import type { I18n, MarqueeCardBase, MarqueeItem, PetitionMarqueeCardData } from './types';

function buildCuratedCard(item: MarqueeItem, base: MarqueeCardBase, i18n: I18n): PetitionMarqueeCardData | undefined {
	switch (item.cardType) {
		case 'SUPPORTER_VOICE':
			return buildSupporterVoiceCard(item, base, i18n);
		case 'STARTER_UPDATE':
		case 'DECISION_MAKER':
		case 'ENDORSEMENT':
			return buildUpdateQuoteCard(item, base, i18n);
		case 'MILESTONE':
			return buildMilestoneCard(item, base, i18n);
		case 'CLOSE_TO_GOAL':
			return buildCloseToGoalCard(item, base, i18n);
		case 'MOMENTUM':
			return buildMomentumCard(item, base, i18n);
		case 'PETITION':
			return buildPetitionCard(item, base, i18n);
		/* istanbul ignore next -- @preserve: unreachable by construction, see the guard below */
		default: {
			// Exhaustiveness guard: a new card type makes `item.cardType` non-never here, forcing a
			// compile error until it is given a builder above. canBuildMarqueeCard already drops an
			// unrecognized type, so nothing reaches this at runtime.
			const exhaustive: never = item.cardType;
			return exhaustive;
		}
	}
}

/**
 * There is no generic card to fall back to, so an entry whose card can't be built
 * is left out. useMarqueeRows cycles the remaining petitions to fill the row.
 *
 * `simple` bypasses the cardType switch entirely, rendering every entry as the simple card.
 */
export function buildCard(
	item: MarqueeItem,
	index: number,
	i18n: I18n,
	{ simple }: { simple: boolean },
): PetitionMarqueeCardData | undefined {
	// getHomepagePageData filters on the same predicate, so shouldShowPetitionMarquee's
	// "true guarantees visible cards" contract holds; re-checking here keeps the two from
	// ever diverging silently
	if (!canBuildMarqueeCard(item)) return undefined;

	const base: MarqueeCardBase = {
		// the curated position, not the petition - a curation can hold the same petition twice
		// with the same card type, and those two cards must stay separately reachable
		key: `${index}:${item.petition.id}:${item.cardType}`,
		petitionId: item.petition.id,
		title: item.summarizedTitle,
		href: `/p/${encodeURIComponent(item.petition.slug)}?source_location=homepage_petition_marquee`,
		relativeTimeLabel: undefined,
	};

	return simple ? buildSimplePetitionCard(item, base, i18n) : buildCuratedCard(item, base, i18n);
}
